import { Loader2 } from 'lucide-react'
import type { UseQueryResult } from '@tanstack/react-query'
import type { HousecallProStatusDto } from '@lib/dto/housecall-pro.dto'
import { Button } from '@/components/ui/button'
import { errorMessage } from '@/helpers/logger'

/**
 * What a Housecall Pro section says while its status is not known yet.
 *
 * A failed status used to take the same path as "not connected", which hides
 * the section. Pending and error stay visible. Once the status has arrived,
 * this renders nothing and the caller decides whether the connection exists.
 */
export function HcpStatusMessage({
  status,
}: {
  status: UseQueryResult<HousecallProStatusDto>
}) {
  if (status.isPending) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4">
        <p className="flex items-center gap-2 text-sm text-muted-text">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking Housecall Pro…
        </p>
      </section>
    )
  }
  if (status.isError) {
    return (
      <section className="space-y-2 rounded-lg border border-default bg-surface p-4">
        <p className="text-sm text-danger">
          {errorMessage(status.error, 'Housecall Pro could not be loaded.')}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void status.refetch()
          }}
        >
          Try again
        </Button>
      </section>
    )
  }
  return null
}
